import {
    useState,
    useEffect,
    useContext,
    useCallback,
    useMemo,
} from 'react';
import { FaUser, FaLock, FaAt } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import Cookies from 'js-cookie';
import { useMsal } from '@azure/msal-react';

import { AuthContext } from '@providers/Auth';

import { getToken } from '@helpers/utils';
import { signUp, TSignUpParams } from '@api/signUp';

import Input from '@components/Input';
import Button from '@components/Button';

import WhiteshieldLogoWhite from '@assets/whiteshield_logo-white.png';
import WhiteshieldLogo from '@assets/whiteshield_logo.png';
import BackgroundDark from '@assets/login-background.jpg';
import BackgroundLight from '@assets/background-light.jpg';
import GoogleCloudLogo from '@assets/google-cloud.svg';
import WhiteshieldIconLogo from '@assets/whiteshield-white-icon.png';
import LoaderImage from '@assets/loader.gif';

import './SignUp.scss';

const SignUp = () => {
    let navigate = useNavigate();

    let location = useLocation();
    const params = new URLSearchParams(location.search);
    const refId = params.get('refId') || params.get('refid');
    if (!refId) {
        navigate('/');
    }

    const darkMode = Cookies.get('darkMode') === 'true';
    const [errorMessage, setErrorMessage] = useState<string>('');
    const defaultErrorFields = useMemo(() => {
        return {
            firstName: false,
            lastName: false,
            email: false,
            password: false,
            confirmPassword: false,
        }
    }, []);
    const [errorFields, setErrorFields] = useState<any>(defaultErrorFields);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSuccessSignup, setIsSuccessSignup] = useState<boolean>(false);

    const formDefaultValue = useMemo(() => {
        return {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            confirmPassword: '',
            googleId: refId || ''
        }
    }, [refId]);

    const [data, setData] = useState<TSignUpParams>(formDefaultValue);

    const { setIsLoggedIn } = useContext(AuthContext);

    const { accounts } = useMsal();


    const inputStyles = {
        marginBottom: 15,
    };

    const inputStylesError = { ...inputStyles, border: "1px solid red" };

    const handleChange = useCallback(e => {
        const { dataset, value, checked, type } = e.target;
        const { id } = dataset;
        const isCheckbox = type === 'checkbox';

        setData(prevState => ({
            ...prevState,
            [id]: isCheckbox ? checked : value,
        }));
    }, []);

    const redirectToLogin = useCallback(() => {
        navigate('/');
    }, [navigate]);


    const validateForm = useCallback(() => {
        // Set error fields to default at the beginning
        setErrorFields(defaultErrorFields);

        // Regex for firstname and lastname (alphanumeric and apostrophes)
        const nameRegex = /^[a-zA-Z0-9']+$/;

        // Regex for email validation
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        // Regex for password validation (minimum 8 characters, 1 uppercase, 1 number, 1 special character)
        const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

        // Array to store error messages
        const errorMessages = [];

        // Validate firstname
        if (!nameRegex.test(data.firstName)) {
            errorMessages.push("Firstname is invalid. Only alphanumeric characters and apostrophes are allowed.");
            setErrorFields((prevState: any) => ({ ...prevState, firstName: true }));
        }

        // Validate lastname
        if (!nameRegex.test(data.lastName)) {
            errorMessages.push("Lastname is invalid. Only alphanumeric characters and apostrophes are allowed.");
            setErrorFields((prevState: any) => ({ ...prevState, lastName: true }));
        }

        // Validate email
        if (!emailRegex.test(data.email)) {
            errorMessages.push("Email is invalid. Please provide a valid email address.");
            setErrorFields((prevState: any) => ({ ...prevState, email: true }));
        }

        // Validate password
        if (!passwordRegex.test(data.password)) {
            errorMessages.push("Password must be at least 8 characters long and include at least one uppercase letter, one number, and one special character.");
            setErrorFields((prevState: any) => ({ ...prevState, password: true }));
        }

        // Validate confirm password
        if (!data.confirmPassword || data.password !== data.confirmPassword) {
            errorMessages.push("Password and confirm password do not match.");
            setErrorFields((prevState: any) => ({ ...prevState, confirmPassword: true }));
        }

        // Check if there are any error messages
        if (errorMessages.length > 0) {
            return {
                valid: false,
                messages: errorMessages
            };
        }

        return { valid: true, messages: ["All fields are valid."] };
    }, [data, setErrorFields, defaultErrorFields]);


    const handleSignUp = useCallback(async () => {
        setIsSuccessSignup(false);
        var validationResult = validateForm();
        if (validationResult.valid) {
            // Call SignUp API
            setErrorMessage("");

            setIsLoading(true);

            var [errorSignUp] = await signUp(data);
            if (errorSignUp) {
                setErrorMessage(errorSignUp);
            }
            else {
                setIsSuccessSignup(true);
                setTimeout(() => {
                    navigate('/');
                }, 3000);
            }

            setIsLoading(false);
        }
        else {
            setErrorMessage(validationResult.messages.join("</br>"));
        }
    }, [validateForm, data, navigate]);

    useEffect(() => {
        const token = getToken();
        if (token) {
            setIsLoggedIn(true);
            navigate('/dashboard');
        }
    }, [navigate, setIsLoggedIn, accounts]);

    return (
        <>
            <div
                className={cn('signup', {
                })}
            >
                <div
                    className="signup__background"
                    style={{
                        backgroundImage: `url(${darkMode ? BackgroundDark : BackgroundLight
                            })`,
                    }}
                >
                    <div className="signup__content-wrapper">
                        <div className="signup__content">
                            <div className="signup__form">
                                <div className="row">
                                    <div>
                                        <img
                                            src={GoogleCloudLogo}
                                            alt="google cloud logo"
                                            width="40px"
                                            height="40px"
                                            className="signup__form__google-logo"
                                        />
                                        <span className="signup__form__separator">|</span>
                                        <img
                                            src={WhiteshieldIconLogo}
                                            alt="google cloud logo"
                                            width="40px"
                                            height="40px"
                                            className="signup__form__whiteshield-logo"
                                        />
                                    </div>
                                    <h2 className="signup__form__title m-2">
                                        Subscribe to Future of Work Navigator
                                    </h2>
                                    <h3 className="signup__form__title mb-4">We're excited to have you on board.</h3>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <Input
                                            data-id="firstName"
                                            placeholder="First Name"
                                            icon={<FaUser />}
                                            style={errorFields.firstName ? inputStylesError : inputStyles}
                                            value={data.firstName}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <Input
                                            data-id="lastName"
                                            placeholder="Last Name"
                                            icon={<FaUser />}
                                            style={errorFields.lastName ? inputStylesError : inputStyles}
                                            value={data.lastName}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-12">
                                        <Input
                                            data-id="email"
                                            placeholder="Email"
                                            icon={<FaAt />}
                                            style={errorFields.email ? inputStylesError : inputStyles}
                                            value={data.email}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <Input
                                            data-id="password"
                                            placeholder="Password"
                                            type="password"
                                            icon={<FaLock />}
                                            style={errorFields.password ? inputStylesError : inputStyles}
                                            value={data.password}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <Input
                                            data-id="confirmPassword"
                                            placeholder="Confirm Password"
                                            type="password"
                                            icon={<FaLock />}
                                            style={errorFields.confirmPassword ? inputStylesError : inputStyles}
                                            value={data.confirmPassword}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <div
                                    className="signup__login"
                                    onClick={redirectToLogin}
                                >
                                    Already have an account?
                                </div>
                                {errorMessage && (
                                    <div className="signup__error-message" dangerouslySetInnerHTML={{ __html: errorMessage }} />
                                )}
                                <div className="mt-3 signup-actions">
                                    {isLoading ?
                                        <div className="signup-actions__loading-image">
                                            <img src={LoaderImage} alt="" width="40px" height="40px" />
                                        </div>
                                        :
                                        (!isSuccessSignup
                                            ?
                                            <>
                                                <Button label="Sign&nbsp;Up" disabled={isLoading} onClick={handleSignUp} invertedColors />
                                            </>
                                            :
                                            <div className="signup__success-message">You have successfully signed up! You will be redirected to login page.</div>
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="signup-footer">
                        <div>Powered by</div>
                        <img
                            src={darkMode ? WhiteshieldLogoWhite : WhiteshieldLogo}
                            alt="company logo"
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default SignUp;
